import { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { DateTable } from './Table'
import { Line } from '@/components/elements/Line'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { AHeading, BHeading } from '@/components/layouts/Ir/Heading'
import { Paragraph } from '@/components/layouts/Ir/Paragraph'

export const ShareholderPage = () => {
  return (
    <Layout>
      <SectionTitle
        color="normal"
        titleText="Interested in becoming an Investor?"
        subTitleText="トライアルの株主になりませんか"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <AHeading>株主になるメリット</AHeading>
        <BHeading>①当社の経営に参加できます</BHeading>
        <Paragraph>
          株主になると、会社の最高意思決定機関である株主総会に出席でき、議決権の行使を通じて、当社の経営に参加することができます。
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 80 }} />
        <BHeading>②配当金を受け取れます</BHeading>
        <Paragraph>
          株主になると、配当金を受領することができます。当社は6月決算であり、毎年6月末が配当基準日となります。
        </Paragraph>
        <Paragraph>
          2027年6月期の期末配当は2027年6月28日（祝日、休日を除く、権利確定日から起算して2営業日前）までに証券会社等を通じてご購入（約定）いただいた方が対象となります。
        </Paragraph>
        <Spacer h={{ sp: 20, pc: 50 }} />
        <DateTable />
        <Spacer h={{ sp: 70, pc: 100 }} />
        <AHeading>株主になるには</AHeading>
        <QuestionContent>
          <QuestionIcon>Q</QuestionIcon>
          <QuestionText>
            <p>株主になるための手続きは、どこで行えばいいですか？</p>
          </QuestionText>
        </QuestionContent>
        <Spacer h={{ pc: 20, sp: 16 }} />
        <AnswerContent>
          <AnswerIcon>A</AnswerIcon>
          <AnswerText>
            <p>
              証券会社を通じて株式をご購入ください。購入には事前に証券会社での口座開設が必要になります。詳しくは、お近くの証券会社窓口やインターネット取引を行っている証券会社にご相談ください。
            </p>
          </AnswerText>
        </AnswerContent>
        <Spacer h={{ pc: 44, sp: 20 }} />
        <Line color="grey" weight={1} direction="horizon"></Line>
        <Spacer h={{ pc: 44, sp: 20 }} />
        <QuestionContent>
          <QuestionIcon>Q</QuestionIcon>
          <QuestionText>
            <p>
              トライアルホールディングスの株式は、いくらから購入できますか？
            </p>
          </QuestionText>
        </QuestionContent>
        <Spacer h={{ pc: 20, sp: 16 }} />
        <AnswerContent>
          <AnswerIcon>A</AnswerIcon>
          <AnswerText>
            <p>当社の株式は、100株を1単元として売買されています。</p>
            <p>
              つまり、株価×100株の金額で、株主になることができます。ただし、ご購入の際には手数料がかかり、証券会社によって異なります。
            </p>
            <p>
              ※ご注意：株価は変動しますので購入の際は最新の株価をご自身でご確認くださいますようお願いいたします。
            </p>
          </AnswerText>
        </AnswerContent>
      </section>
    </Layout>
  )
}

ShareholderPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '/ir/' },
    { name: '個人投資家の皆様へ', path: '/ir/investor/' },
    { name: 'トライアルの株主になりませんか', path: '' },
  ]

  return (
    <>
      <Head
        title="トライアルの株主になりませんか"
        description="トライアルホールディングスの株主へのご案内を掲載しています。"
        ogp={{ url: 'ir/investor/shareholder/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const QuestionContent = styled.div`
  display: flex;
  gap: 10px;
`

const QuestionIcon = styled.div`
  background: ${theme.colors.trialBlue};
  color: white;
  font-family: 'Rubik';
  font-style: normal;
  font-size: 18px;
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 20px;
  padding: 6px 8.5px;
`

const QuestionText = styled.div`
  p {
    font-size: 18px;
    line-height: 1.5;
    color: #002d65;
    font-weight: 700;
  }

  @media screen and (max-width: 1047px) {
    p {
      font-size: 16px;
    }
  }
`

const AnswerContent = styled.div`
  display: flex;
  gap: 10px;
`

const AnswerIcon = styled.div`
  background: ${theme.colors.grey};
  color: white;
  font-family: 'Rubik';
  font-style: normal;
  font-size: 18px;
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 20px;
  padding: 6px 8.5px;
`

const AnswerText = styled.div`
  p {
    font-size: 16px;
    line-height: 1.8;
  }
  @media screen and (max-width: 1047px) {
    p {
      font-size: 15px;
    }
  }
`
